import SVToasterThumbnails from '@apps/www/src/www/components/SVToasterThumbnails';
import SVIconButton, { SVIconButtonSIZES } from '@pkgs/shared-client/components/SVIconButton';
import SVLink from '@pkgs/shared-client/components/SVLink';
import cn from '@pkgs/shared-client/helpers/cn';
import IconCloseSVG from '@pkgs/shared-client/img/icon-close-inlined.svg';
import { toast as sonnerToast } from 'sonner';
import { twMerge } from 'tailwind-merge';
import SVButton from './SVButton';

export const TOAST_TYPES = {
	SUCCESS: 'success',
	ERROR: 'error',
	LOADING: 'loading',
	ADD_TO_BOARD: 'add_to_board',
	BOARD_CREATED: 'board_created',
} as const;

interface ToastProps {
	id?: string | number;
	user?: { avatarURL?: string; name?: string; username?: string };
	users?: { avatarURL?: string; name?: string; username?: string }[];
	content?: string;
	title?: string;
	description?: string;
	type?: 'success' | 'error' | 'loading' | 'add_to_board' | 'board_created';
	button?: {
		label: string;
		onClick?: () => void;
		to?: string;
	};
	disableCloseButton?: boolean;
	thumbnails?: Array<{ _id: string; image: { thumbnail: string } }> | string[];
	avatarURL?: string;
}

function _ToastContent({
	type,
	title,
	description,
	user,
	users,
	content,
}: {
	type?: string;
	title?: string;
	description?: string;
	user?: ToastProps['user'];
	users?: ToastProps['users'];
	content?: ToastProps['content'];
}) {
	if (type === 'success' && content) {
		return (
			<div>
				<p className="text-sm font-medium text-gray-700">
					<span className="text-gray-500">{content + ' '} </span>
					{users?.length ? (
						<span className="font-semibold text-gray-900">
							{(() => {
								const names = users.map((user) => user.name).filter(Boolean);
								if (names.length === 1) {
									return names[0];
								} else if (names.length === 2) {
									return (
										<>
											{names[0]} <span className="text-gray-500">and</span>{' '}
											{names[1]}
										</>
									);
								} else {
									const last = names.pop();
									return (
										<>
											{names.join(', ')}{' '}
											<span className="text-gray-500">and</span> {last}
										</>
									);
								}
							})()}
						</span>
					) : user ? (
						<span className="font-semibold text-gray-900">{user.name}</span>
					) : null}
				</p>
			</div>
		);
	}

	if (type === 'add_to_board' && title) {
		return (
			<div className="max-w-[120px]">
				<p className="line-clamp-2 text-sm font-medium text-gray-700">
					<span className="text-gray-500">Added to </span>
					<span className="font-semibold text-gray-900"> {title}</span>
				</p>
			</div>
		);
	}

	if (type === 'board_created' && title) {
		return (
			<div>
				<p className="line-clamp-2 text-sm font-medium text-gray-700">
					<span className="text-gray-500">Board</span>
					<span className="font-semibold text-gray-900"> {title}</span>
					<span className="text-gray-500"> created</span>
				</p>
			</div>
		);
	}

	return (
		<div className="line-clamp-2 flex max-w-[180px] flex-col">
			{title && <p className="text-sm font-medium text-gray-700">{title}</p>}
			{description && <p className="mt-1 text-xs text-gray-500">{description}</p>}
		</div>
	);
}

function Toast({ id, ...props }: ToastProps & { id: string | number }) {
	const hasThumbnails = Boolean(props.thumbnails?.length);

	return (
		<div
			className={twMerge(
				'bg-primary hover:bg-primary/90 relative flex items-center rounded-xl p-4 shadow-lg ring-1 ring-black/5',
				!props.disableCloseButton ? 'pr-5' : props.button ? 'pr-4' : 'pr-0',
			)}
		>
			{hasThumbnails && (
				<SVToasterThumbnails thumbnails={props.thumbnails} avatarURL={props.avatarURL} />
			)}
			<div className={cn('flex-1', hasThumbnails && 'ml-4')}>
				<_ToastContent
					type={props.type}
					title={props.title}
					description={props.description}
					user={props.user}
					users={props.users}
					content={props.content}
				/>
			</div>

			<div className="ml-4 flex items-center space-x-2">
				{props.button && (
					<SVButton
						className="type-small text-primary rounded-xl bg-gray-800 px-5 py-3.5 font-medium transition-colors hover:bg-gray-700"
						{...(props.button.to
							? { Component: SVLink, to: props.button.to }
							: { onClick: props.button.onClick })}
					>
						{props.button.label}
					</SVButton>
				)}
			</div>
			{!props.disableCloseButton && (
				<div className="absolute right-3 top-3">
					<SVIconButton
						onClick={() => sonnerToast.dismiss(id)}
						src={IconCloseSVG}
						label="Close"
						size={SVIconButtonSIZES.SMALL}
						className="text-gray-500"
						iconClassName="w-2.5 h-2.5"
						disableHover
					/>
				</div>
			)}
		</div>
	);
}

const createToast = (
	props: ToastProps,
	duration = 4000,
	position:
		| 'top-left'
		| 'top-right'
		| 'bottom-left'
		| 'bottom-right'
		| 'top-center'
		| 'bottom-center' = 'top-right',
) => {
	return sonnerToast.custom((id) => <Toast id={id} {...props} />, {
		duration,
		position,
	});
};
export const toast = {
	success: (title: string, options?: Partial<ToastProps>) =>
		createToast({ type: 'success', title, ...options }, 3000, 'bottom-center'),

	error: (title: string, options?: Partial<ToastProps>) =>
		createToast({ type: 'error', title, ...options }, 5000, 'bottom-center'),

	loading: (title: string, options?: Partial<ToastProps>) =>
		createToast(
			{
				type: 'loading',
				title,
				disableCloseButton: true,
				...options,
			},
			Infinity,
			'bottom-center',
		),
	addToBoard: (boardName: string, thumbnails: string[], options?: Partial<ToastProps>) =>
		createToast(
			{
				type: 'add_to_board',
				title: boardName,
				thumbnails,
				disableCloseButton: true,

				...options,
			},
			2000,
			'bottom-center',
		),
	CreateBoard: (boardName: string, options?: Partial<ToastProps>) =>
		createToast(
			{
				type: 'board_created',
				title: boardName,
				thumbnails: options?.thumbnails,
				disableCloseButton: true,
				...options,
			},
			2000,
			'bottom-center',
		),
};

export default Toast;
